import { noop } from "lodash";
import type { ReactElement } from "react";
import { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";

import {
  SpaceSelectComponent,
  type SpaceSelectComponentProps,
} from "./Component";

import "./styles.scss";

export interface SpaceSelectProps extends SpaceSelectComponentProps {
  defaultValue?: number;
  name?: string;
  onChange?: () => void;
  rules?: Record<string, unknown>;
  trigger?: ReactElement;
}

interface ControllerRenderProps {
  field: {
    value: number | null;
    onChange: (value: unknown) => void;
  };
}

export const SpaceSelect = ({
  name = "",
  rules = {},
  defaultValue,
  onChange = noop,
  ...passThroughProps
}: SpaceSelectProps) => {
  const { control, setValue } = useFormContext();

  useEffect(() => {
    // Default value may take some time to load,
    // We're setting the value once it changes (becomes available)
    if (defaultValue) {
      setValue(name, defaultValue);
    }
  }, [defaultValue]);

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      defaultValue={defaultValue}
      render={({ field }: ControllerRenderProps) => (
        <SpaceSelectComponent
          {...passThroughProps}
          value={field.value}
          onChange={value => {
            field.onChange(value);
            onChange?.();
            setValue(name, value, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });
          }}
        />
      )}
    />
  );
};

SpaceSelect.displayName = "Form.SpaceSelect";
